<script setup lang="ts">
import { UiChip, UiLinkField, UiText } from "@shopexperts/shared";
import AccessKeyPanel from "./AccessKeyPanel.vue";
import type { SatelliteContactSite, SatelliteContactSiteStats } from "@shopexperts/legacy-api";
import {
    formatSatelliteDate,
    formatSubmissionCount,
    formatStatLabel,
    STAT_KEYS,
} from "@/app/composables/satellite-contact-sites/satelliteSiteFormatters";

defineProps<{
    site: SatelliteContactSite | null | undefined;
    stats?: SatelliteContactSiteStats | null;
    isLoading?: boolean;
    highlightAccessKey?: boolean;
}>();
</script>

<template>
    <div v-if="isLoading" class="py-8 text-center">
        <UiText as="p" variant="p" class="text-se-gray-600" text="Loading site details..." />
    </div>

    <div v-else-if="site" class="flex flex-col gap-6">
        <div class="grid gap-4 sm:grid-cols-2">
            <div>
                <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs" text="Site name" />
                <UiText as="p" variant="p" class="font-semibold text-se-dark" :text="site.name" />
            </div>
            <div>
                <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs" text="Status" />
                <div class="mt-1">
                    <UiChip
                        :text="site.is_active ? 'Active' : 'Inactive'"
                        :variant="site.is_active ? 'success' : 'neutral'"
                        size="sm"
                    />
                </div>
            </div>
            <div>
                <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs" text="Submissions" />
                <UiText as="p" variant="p" class="font-semibold text-se-dark" :text="formatSubmissionCount(site.submissions_count)" />
            </div>
            <div>
                <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs" text="Last used" />
                <UiText as="p" variant="p" class="font-semibold text-se-dark" :text="formatSatelliteDate(site.last_used_at)" />
            </div>
            <div>
                <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs" text="Created" />
                <UiText as="p" variant="p" class="font-semibold text-se-dark" :text="formatSatelliteDate(site.created_at)" />
            </div>
            <div>
                <UiText as="p" variant="caption-normal" class="text-se-gray-500 uppercase tracking-wider text-xs" text="Updated" />
                <UiText as="p" variant="p" class="font-semibold text-se-dark" :text="formatSatelliteDate(site.updated_at)" />
            </div>
        </div>

        <UiLinkField label="Website URL" :value="site.website_url" copy-label="Copy URL" />

        <AccessKeyPanel
            v-if="site.access_key"
            :access-key="site.access_key"
            :highlight="highlightAccessKey"
            :description="highlightAccessKey
                ? 'Copy the new key to the satellite site now. The previous key stops working immediately.'
                : 'Returned by the admin API on site detail requests. Copy it into the satellite site configuration.'"
        />

        <div v-if="stats" class="rounded-md border border-se-gray-200 bg-se-gray-50 p-4">
            <UiText as="p" variant="h6" class="mb-3 font-semibold text-se-dark" text="Submission stats" />
            <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div v-for="key in STAT_KEYS" :key="key" class="rounded-md bg-white px-3 py-2 border border-se-gray-200">
                    <UiText
                        as="p"
                        variant="caption-normal"
                        class="text-se-gray-500 uppercase tracking-wider text-xs"
                        :text="formatStatLabel(key)"
                    />
                    <UiText as="p" variant="p-small" class="mt-1 font-semibold tabular-nums text-se-dark" :text="String(stats[key])" />
                </div>
            </div>
        </div>
    </div>
</template>
